import { EXTERNAL_FOLDER } from '@minty/shared/email';
import { useSuspenseQuery } from '@tanstack/react-query';
import {
  Link,
  Outlet,
  createFileRoute,
  stripSearchParams,
  useNavigate,
} from '@tanstack/react-router';
import { Plus, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { folderIcon, folderLabel } from './-components/folders';
import type { MailFolder } from './-components/types';
import { formatUnreadCount } from '~/components/list-panel-states';
import { Input } from '~/components/ui/input';
import { Skeleton } from '~/components/ui/skeleton';
import { useTRPC } from '~/lib/trpc';

// Search spans the whole mailbox, not one folder: it lives in the URL here,
// and clearing it returns to whichever folder is underneath.
const defaultMailSearch = { q: '' };

const mailSearchSchema = z.object({
  q: z.string().catch('').default(''),
});

export const Route = createFileRoute('/_dashboard/mail')({
  validateSearch: mailSearchSchema,
  search: { middlewares: [stripSearchParams(defaultMailSearch)] },
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData(
      context.trpc.mailbox.views.listFolders.queryOptions(),
    );
  },
  pendingComponent: MailLayoutPending,
  component: MailLayoutRoute,
});

const SPECIAL_ORDER = ['\\Inbox', '\\Sent', '\\Drafts', '\\Junk', '\\Trash'];

const sidebarItemClassName =
  'flex h-10 w-full items-center gap-2 rounded-full border border-transparent bg-background px-3.5 py-2.5 text-sm font-medium leading-5 text-muted-foreground hover:border-border hover:bg-accent hover:text-foreground data-[status=active]:border-border data-[status=active]:bg-accent data-[status=active]:text-foreground';

function MailLayoutRoute() {
  const { t } = useTranslation('mailbox');
  const trpc = useTRPC();
  const { data: folders } = useSuspenseQuery(
    trpc.mailbox.views.listFolders.queryOptions(),
  );

  return (
    <>
      <aside className="flex w-64 shrink-0 flex-col items-start self-stretch overflow-auto border-r border-border bg-background">
        <div className="flex flex-col items-start gap-3 self-stretch border-b border-border px-3 py-5">
          <Link to="/mail/new-message" className={sidebarItemClassName}>
            <Plus className="size-4 shrink-0" strokeWidth={2} />
            <span className="min-w-0 flex-1 truncate">{t('New Message')}</span>
          </Link>
          <MailSearchField />
        </div>
        <nav className="flex flex-col items-start gap-1 self-stretch px-3 py-5">
          {sortFolders(folders).map(({ depth, folder }) => {
            const Icon = folderIcon(folder);

            return (
              <Link
                key={folder.path}
                to="/mail/$folder"
                params={{ folder: folder.path }}
                activeOptions={{ includeSearch: false }}
                className={sidebarItemClassName}
                style={{ paddingLeft: `${0.875 + depth}rem` }}
              >
                <Icon className="size-4 shrink-0" strokeWidth={2} />
                <span className="min-w-0 flex-1 truncate">
                  {folderLabel(folder, t)}
                </span>
                {folder.unreadCount > 0 && (
                  <span className="ml-auto shrink-0 text-sm font-medium leading-5 tabular-nums">
                    {formatUnreadCount(folder.unreadCount)}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>
      </aside>
      <div className="min-w-0 flex-1 overflow-hidden">
        <Outlet />
      </div>
    </>
  );
}

function MailSearchField() {
  const { t } = useTranslation('mailbox');
  const { q } = Route.useSearch();
  const navigate = useNavigate();
  const setQuery = (next: string) =>
    void navigate({
      to: '.',
      search: (previous) => ({ ...previous, q: next }),
      replace: true,
    });

  return (
    <div className="relative w-full">
      <Search
        className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
        strokeWidth={2}
      />
      <Input
        type="search"
        value={q}
        onChange={(event) => setQuery(event.currentTarget.value)}
        placeholder={t('Search')}
        aria-label={t('Search mail')}
        className="h-10 rounded-full pl-9 shadow-none"
      />
    </div>
  );
}

// Special folders first, in a fixed order; the rest as a tree by path.
function sortFolders(folders: Array<MailFolder>) {
  const byPath = new Map(folders.map((folder) => [folder.path, folder]));
  const depthOf = (folder: MailFolder): number => {
    const parent = folder.parentPath ? byPath.get(folder.parentPath) : null;
    return parent ? depthOf(parent) + 1 : 0;
  };
  // The mailbox's own folders first; External, the app's folder, last.
  const rank = (folder: MailFolder) => {
    if (folder.specialUse === EXTERNAL_FOLDER) {
      return SPECIAL_ORDER.length + 1;
    }
    const index = folder.specialUse
      ? SPECIAL_ORDER.indexOf(folder.specialUse)
      : -1;
    return index === -1 ? SPECIAL_ORDER.length : index;
  };

  return [...folders]
    .sort((a, b) => rank(a) - rank(b) || a.path.localeCompare(b.path))
    .map((folder) => ({
      folder,
      depth: folder.specialUse ? 0 : depthOf(folder),
    }));
}

function MailLayoutPending() {
  return (
    <>
      <aside
        className="flex w-64 shrink-0 flex-col gap-3 border-r border-border bg-background px-3 py-5"
        aria-busy="true"
      >
        <Skeleton className="h-10 w-full rounded-full" />
        <Skeleton className="h-10 w-full rounded-full" />
        <Skeleton className="h-10 w-full rounded-full" />
        <Skeleton className="h-10 w-full rounded-full" />
      </aside>
      <div className="min-w-0 flex-1 bg-background" />
    </>
  );
}
